import { getComputedEventStatus } from "../lib/eventPublicStatus";
import { isOngoingSupportStatus } from "../lib/supportTickets";

export const CARD_RANGE_OPTIONS = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "3m", label: "Last 3 months" },
  { value: "6m", label: "Last 6 months" },
  { value: "12m", label: "Last 12 months" },
  { value: "all", label: "All time" },
];

export const STAT_PANEL_KEYS = ["revenue", "ticketsSold", "activeEvents", "supportTickets"];

export const getRangeLabel = (rangeKey) => {
  const found = CARD_RANGE_OPTIONS.find((item) => item.value === rangeKey);
  return found?.label || "Last 30 days";
};

export const getRangeStartDate = (rangeKey, now = new Date()) => {
  if (rangeKey === "all") return null;

  const start = new Date(now);
  if (rangeKey === "7d") {
    start.setDate(now.getDate() - 7);
  } else if (rangeKey === "30d") {
    start.setDate(now.getDate() - 30);
  } else if (rangeKey === "3m") {
    start.setMonth(now.getMonth() - 3);
  } else if (rangeKey === "6m") {
    start.setMonth(now.getMonth() - 6);
  } else {
    start.setMonth(now.getMonth() - 12);
  }
  return start;
};

export const isDateInsideRange = (dateInput, rangeKey, now = new Date()) => {
  if (!dateInput) return false;
  const parsed = new Date(dateInput);
  if (Number.isNaN(parsed.getTime())) return false;
  if (parsed > now) return false;

  const start = getRangeStartDate(rangeKey, now);
  return !start || parsed >= start;
};

export const isRefundedTicket = (ticket) => Boolean(ticket?.refunded_at);

export const buildTierIndex = (tiers = []) => {
  const tierById = {};
  tiers.forEach((tier) => {
    tierById[tier.id] = tier;
  });
  return tierById;
};

export const filterTicketsForEvent = (tickets = [], tierById = {}, eventId = "") => {
  if (!eventId) return tickets;
  return tickets.filter((ticket) => tierById[ticket.ticket_tier_id]?.event_id === eventId);
};

export const getSharedRange = (ranges = {}) => {
  const values = STAT_PANEL_KEYS.map((key) => ranges[key]);
  return values.every((value) => value === values[0]) ? values[0] : "";
};

export const computeDashboardStats = ({
  events = [],
  tiers = [],
  tickets = [],
  supportTickets = [],
  ranges = {},
  eventId = "",
  now = new Date(),
}) => {
  const tierById = buildTierIndex(tiers);
  const scopedTickets = filterTicketsForEvent(tickets, tierById, eventId);
  const ticketPrice = (ticket) => Number(tierById[ticket.ticket_tier_id]?.price || 0);

  let revenue = 0;
  let ticketsSold = 0;
  let soldAllTime = 0;
  let checkedIn = 0;
  let refunds = 0;
  let refundedAmount = 0;

  scopedTickets.forEach((ticket) => {
    if (isRefundedTicket(ticket)) {
      if (isDateInsideRange(ticket.refunded_at, ranges.supportTickets, now)) {
        refunds += 1;
        refundedAmount += ticketPrice(ticket);
      }
      return;
    }

    soldAllTime += 1;
    if (isDateInsideRange(ticket.created_at, ranges.revenue, now)) {
      revenue += ticketPrice(ticket);
    }
    if (isDateInsideRange(ticket.created_at, ranges.ticketsSold, now)) {
      ticketsSold += 1;
    }
    if (
      ticket.is_validated &&
      isDateInsideRange(ticket.validated_at || ticket.created_at, ranges.activeEvents, now)
    ) {
      checkedIn += 1;
    }
  });

  const capacityTotal = eventId
    ? tiers
        .filter((tier) => tier.event_id === eventId)
        .reduce((sum, tier) => sum + Number(tier.quantity_total || 0), 0)
    : 0;
  const capacityPercent = capacityTotal > 0 ? Math.round((soldAllTime / capacityTotal) * 100) : 0;

  const activeEvents = events.filter(
    (event) =>
      getComputedEventStatus(event) === "published" &&
      isDateInsideRange(event.created_at, ranges.activeEvents, now)
  ).length;

  const openSupportTickets = supportTickets.filter(
    (ticket) =>
      isOngoingSupportStatus(ticket.status) &&
      isDateInsideRange(ticket.created_at, ranges.supportTickets, now)
  ).length;

  return {
    revenue,
    ticketsSold,
    activeEvents,
    supportTickets: openSupportTickets,
    soldAllTime,
    capacityTotal,
    capacityPercent,
    checkedIn,
    refunds,
    refundedAmount,
  };
};
